import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import * as consent from '@owl-nest/consent'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import * as env from '@owl-nest/config'

import * as plume from '@ulule/owl-kit-components/next'

import { RootState } from '../../../reducers'
import * as projectModel from '../../models/project'
import { SplashTitle } from './components/SplashTitle'

export function Context(): React.ReactElement {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const userConfig = env.useUserEnv()
  const lang = userConfig.lang

  const title = model.project.name<projectModel.ExtraFields>(project)
  const subtitle = model.project.subtitle<projectModel.ExtraFields>(project)
  const mainImage =
    project?.coming_soon?.image?.[lang]?.versions?.['large']?.url ??
    model.project.mainImage<projectModel.ExtraFields>(project)
  const video = project?.coming_soon?.video?.[lang] ?? model.project.video<projectModel.ExtraFields>(project)

  return (
    <ContextWrapper $hasVideo={video !== undefined}>
      <Information>
        <SplashTitle text={t('Coming soon!')} />

        <plume.styles.heading.L asDesktop="XL">{title}</plume.styles.heading.L>

        {subtitle && <plume.styles.copy.M.light>{subtitle}</plume.styles.copy.M.light>}
      </Information>

      {video && (
        <ProjectVideoContainer>
          <ProjectVideo
            consentWall={consent.media.Wall}
            placeholder={mainImage ?? video?.thumbnail_url}
            srcSet={mainImage ?? video?.thumbnail_url}
            videoHtml={video.html}
          />
        </ProjectVideoContainer>
      )}
      {!video && mainImage && <ProjectImage src={mainImage} />}
    </ContextWrapper>
  )
}

const ProjectImage = styled.img`
  border-radius: 4px;
  display: block;
  height: 100%;
  width: 97%;
  aspect-ratio: 30 / 17;
  object-fit: cover;
  margin-left: 8px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 50%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 460px;
    margin-left: 16px;
    width: 100%;
    box-shadow: ${plume.COLORS.PRIMARY_GREY_000} -16px 16px 0px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    max-width: 600px;
  }
`

const ProjectVideo = styled(plume.VideoLoader)`
  display: block;
  width: 100%;
`

const ProjectVideoContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  height: fit-content;
  width: 100%;

  img {
    aspect-ratio: 30 / 17;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 50%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    width: 460px;
    margin-left: 16px;
    box-shadow: ${plume.COLORS.PRIMARY_GREY_000} -16px 16px 0px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    width: 600px;
  }
`

const Information = styled.div`
  margin-bottom: 20px;
  min-width: 260px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-right: 15px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: 0px;
    max-width: 400px;
    margin-right: 35px;
  }
`

const ContextWrapper = styled.div<{ $hasVideo: boolean }>`
  padding: 16px;

  ${plume.styles.copy.M.light} {
    margin-top: 8px;
  }

  ${SplashTitle} {
    display: block;
    margin: 10px auto 25px auto;
    width: fit-content;
  }

  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    align-items: center;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
    justify-content: space-between;
    padding: 20px;

    ${({ $hasVideo }) => {
      if ($hasVideo) {
        return css`
          min-width: 630px;
        `
      }
    }}
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 1210px;
    margin: 0px;
    margin-top: 60px;
    margin-bottom: 60px;
    position: relative;

    ${SplashTitle} {
      position: absolute;
      top: 10px;
    }

    ${plume.styles.heading.L} {
      margin-top: 40px;
      margin-bottom: 16px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 60px 90px 0px;
  }
`
